import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { api } from '../api';
const API_BASE = process.env.REACT_APP_API_URL || '/api';

function ChangePasswordCard() {
  const [open, setOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (newPw !== confirmPw) { setError('New passwords do not match.'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ currentPassword: currentPw, newPassword: newPw }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); setSaving(false); return; }
      setSuccess(true);
      setOpen(false);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setSaving(false);
    } catch (err) {
      setError('Failed to change password.'); setSaving(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <div className="card-header"><h3>Security</h3></div>
      <div className="card-body">
        {success && (
          <div style={{ background: '#dcfce7', color: '#15803d', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '12px', border: '1px solid #bbf7d0' }}>
            Password changed successfully.
          </div>
        )}
        {!open ? (
          <button className="btn btn-secondary btn-sm" onClick={() => { setOpen(true); setSuccess(false); }}>Change Password</button>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '12px', border: '1px solid #fecaca' }}>
                {error}
              </div>
            )}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Current Password</label>
              <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} required autoComplete="current-password"
                style={{ width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid var(--color-gray-200)', borderRadius: '6px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>New Password</label>
              <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required autoComplete="new-password"
                placeholder="Min 8 chars, uppercase, lowercase, number, special"
                style={{ width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid var(--color-gray-200)', borderRadius: '6px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>Confirm New Password</label>
              <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required autoComplete="new-password"
                style={{ width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid var(--color-gray-200)', borderRadius: '6px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving...' : 'Update Password'}</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setOpen(false); setError(null); }}>Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function UptimeBar({ days }) {
  const containerRef = useRef(null);
  if (!days || days.length === 0) return null;
  return (
    <div ref={containerRef} style={{ display: 'flex', gap: '1px', alignItems: 'flex-end', height: '32px', marginTop: '12px' }}>
      {days.map((d, i) => {
        const pct = d.percentage;
        const color = pct === 100 ? '#22c55e' : pct >= 95 ? '#eab308' : '#ef4444';
        return (
          <div
            key={i}
            title={`${d.date}: ${pct.toFixed(1)}% uptime`}
            style={{
              width: '3px',
              height: '100%',
              backgroundColor: color,
              borderRadius: '1px',
              cursor: 'pointer',
              opacity: 0.9,
            }}
          />
        );
      })}
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [statusData, setStatusData] = useState(null);
  const [currentTheme, setCurrentTheme] = useState(() => localStorage.getItem('bidboard-theme') || 'light');
  const [uptimeHistory, setUptimeHistory] = useState(null);
  const [changelog, setChangelog] = useState(null);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [rememberSort, setRememberSort] = useState(() => localStorage.getItem('bidboard-remember-sort') === 'true');

  // Memorial Day visibility is controlled from /admin/themes. App.js stashes
  // the current state on window.__memorialThemeState at app mount; we read
  // that here to decide whether to show Memorial Day in the theme picker.
  const memorialState = typeof window !== 'undefined' && window.__memorialThemeState
    ? window.__memorialThemeState
    : { state: 'hidden', appliedAt: null };

  useEffect(() => {
    fetch(`${API_BASE}/status`).then(r => r.json()).then(setStatusData).catch(() => {});
    fetch(`${API_BASE}/uptime-history`).then(r => r.json()).then(setUptimeHistory).catch(() => {});
  }, []);

  useEffect(() => {
    if (changelogOpen && !changelog) {
      fetch(`${API_BASE}/changelog?limit=30`).then(r => r.json()).then(setChangelog).catch(() => {});
    }
  }, [changelogOpen, changelog]);

  return (
    <>
    <div className="top-bar">
      <h2>Settings</h2>
    </div>
    <div className="page-content">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
      {/* Left column */}
      <div>

      {/* Personal Preferences — visible to everyone */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h3>Preferences</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--color-gray-100)' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 500 }}>Default Dashboard View</div>
              <div style={{ fontSize: '12px', color: '#777' }}>Choose what you see when you open BidBoard</div>
            </div>
            <select style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--color-gray-200)', fontSize: '13px' }}
              defaultValue={localStorage.getItem('bidboard-default-view') || 'mine'}
              onChange={e => localStorage.setItem('bidboard-default-view', e.target.value)}>
              <option value="mine">My Dashboard</option>
              <option value="company">Company Dashboard</option>
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--color-gray-100)' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 500 }}>Default Calendar View</div>
              <div style={{ fontSize: '12px', color: '#777' }}>Starting view when opening the calendar</div>
            </div>
            <select style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--color-gray-200)', fontSize: '13px' }}
              defaultValue={localStorage.getItem('bidboard-calendar-view') || 'month'}
              onChange={e => localStorage.setItem('bidboard-calendar-view', e.target.value)}>
              <option value="month">Month</option>
              <option value="week">Week</option>
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 500 }}>Projects Per Page</div>
              <div style={{ fontSize: '12px', color: '#777' }}>Number of projects shown in table view</div>
            </div>
            <select style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--color-gray-200)', fontSize: '13px' }}
              defaultValue={localStorage.getItem('bidboard-page-size') || '50'}
              onChange={e => localStorage.setItem('bidboard-page-size', e.target.value)}>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="all">All</option>
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 500 }}>Remember Sort Order</div>
              <div style={{ fontSize: '12px', color: '#777' }} title="When enabled, clicking a column header to sort will save your preference. Next time you open the projects table, it will automatically sort by your last selected column and direction.">When you sort a column, remember it for next time</div>
            </div>
            <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}>
              <input type="checkbox" style={{ opacity: 0, width: 0, height: 0 }}
                checked={rememberSort}
                onChange={e => { const c = e.target.checked; setRememberSort(c); localStorage.setItem('bidboard-remember-sort', c ? 'true' : 'false'); }} />
              <span style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: rememberSort ? '#111' : '#ccc',
                borderRadius: '12px', transition: '0.2s',
              }}>
                <span style={{
                  position: 'absolute', height: '18px', width: '18px', left: rememberSort ? '22px' : '3px',
                  bottom: '3px', backgroundColor: 'white', borderRadius: '50%', transition: '0.2s',
                }} />
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Account Info */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h3>Account</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--color-gray-100)' }}>
            <span style={{ fontSize: '12px', color: '#777' }}>Name</span>
            <span style={{ fontSize: '13px', fontWeight: 500 }}>{user?.displayName}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--color-gray-100)' }}>
            <span style={{ fontSize: '12px', color: '#777' }}>Username</span>
            <span style={{ fontSize: '13px', fontFamily: 'monospace' }}>{user?.username}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--color-gray-100)' }}>
            <span style={{ fontSize: '12px', color: '#777' }}>Title</span>
            <span style={{ fontSize: '13px' }}>{user?.title || '-'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--color-gray-100)' }}>
            <span style={{ fontSize: '12px', color: '#777' }}>Email</span>
            <span style={{ fontSize: '13px' }}>{user?.email || '-'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
            <span style={{ fontSize: '12px', color: '#777' }}>Role</span>
            <span style={{ fontSize: '13px', fontWeight: 500, textTransform: 'capitalize' }}>{user?.role}</span>
          </div>
        </div>
      </div>

      <ChangePasswordCard />

      {/* Admin-only hub */}
      {user?.isAdmin && <>
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Admin Tools</h3>
        </div>
        <div className="card-body">
          <div style={{ fontSize: '12px', color: '#777', marginBottom: '12px', lineHeight: 1.5 }}>
            Admin-only surfaces. Requires administrator access — executives and regular users don&apos;t see this card.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
            <Link to="/admin/users" className="admin-tool-tile">
              <div className="admin-tool-tile-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div className="admin-tool-tile-body">
                <div className="admin-tool-tile-title">Manage Users</div>
                <div className="admin-tool-tile-desc">Create, edit, and manage user accounts</div>
              </div>
            </Link>
            <Link to="/admin/ai" className="admin-tool-tile">
              <div className="admin-tool-tile-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2a3 3 0 0 0-3 3v1H7a3 3 0 0 0-3 3v1H3a2 2 0 0 0 0 4h1v1a3 3 0 0 0 3 3h2v1a3 3 0 0 0 6 0v-1h2a3 3 0 0 0 3-3v-1h1a2 2 0 0 0 0-4h-1V9a3 3 0 0 0-3-3h-2V5a3 3 0 0 0-3-3z"/>
                </svg>
              </div>
              <div className="admin-tool-tile-body">
                <div className="admin-tool-tile-title">AI Configuration</div>
                <div className="admin-tool-tile-desc">Claude features toggle + API key + test</div>
              </div>
            </Link>
            <Link to="/admin/reporting" className="admin-tool-tile">
              <div className="admin-tool-tile-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18"/><polyline points="7 14 11 10 15 14 21 8"/>
                </svg>
              </div>
              <div className="admin-tool-tile-body">
                <div className="admin-tool-tile-title">Reporting</div>
                <div className="admin-tool-tile-desc">Standard / Advanced mode + Power BI config</div>
              </div>
            </Link>
            <Link to="/admin/themes" className="admin-tool-tile">
              <div className="admin-tool-tile-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="13.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="10.5" r="2.5"/><circle cx="8.5" cy="7.5" r="2.5"/><circle cx="6.5" cy="12.5" r="2.5"/><path d="M12 2a10 10 0 0 0 0 20c1 0 2-.5 2-2 0-1-.5-1.5-.5-2.5 0-1 .5-1.5 1.5-1.5h1.5a4 4 0 0 0 4-4 9 9 0 0 0-8.5-10z"/>
                </svg>
              </div>
              <div className="admin-tool-tile-body">
                <div className="admin-tool-tile-title">Themes</div>
                <div className="admin-tool-tile-desc">Memorial Day + future platform-wide skins</div>
              </div>
            </Link>
            <Link to="/admin/alpha-features" className="admin-tool-tile">
              <div className="admin-tool-tile-icon" style={{ background: '#fef3c7', color: '#92400e' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                </svg>
              </div>
              <div className="admin-tool-tile-body">
                <div className="admin-tool-tile-title">Features</div>
                <div className="admin-tool-tile-desc">Toggle feature modules (Bid Leveling, etc.)</div>
              </div>
            </Link>
            <Link to="/admin/buddies" className="admin-tool-tile">
              <div className="admin-tool-tile-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9"/><circle cx="9" cy="10" r="1.5" fill="currentColor"/><circle cx="15" cy="10" r="1.5" fill="currentColor"/><path d="M9 15c.8.8 1.9 1.3 3 1.3s2.2-.5 3-1.3"/>
                </svg>
              </div>
              <div className="admin-tool-tile-body">
                <div className="admin-tool-tile-title">Buddy Admin</div>
                <div className="admin-tool-tile-desc">Per-user tamagotchi state + species picker</div>
              </div>
            </Link>
            {statusData?.database?.database && statusData.database.database !== 'CMC_Precon' && (
            <button
              className="admin-tool-tile"
              disabled={snapshotting}
              style={{ textAlign: 'left', cursor: snapshotting ? 'wait' : 'pointer', opacity: snapshotting ? 0.6 : 1 }}
              onClick={async () => {
                if (snapshotting) return;
                if (!window.confirm('Snapshot prod database to this environment?\n\nThis will overwrite the current environment\'s database with an exact copy of prod. Any uncommitted local data will be lost. The operation can take several minutes — leave this tab open.')) return;
                setSnapshotting(true);
                try {
                  const result = await api.snapshotProdToDev();
                  alert(`Snapshot complete.\n\n${result.source} → ${result.target} in ${result.elapsedSec}s at ${new Date(result.timestamp).toLocaleString()}`);
                } catch (err) {
                  alert('Snapshot failed: ' + err.message);
                } finally {
                  setSnapshotting(false);
                }
              }}
            >
              <div className="admin-tool-tile-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/>
                </svg>
              </div>
              <div className="admin-tool-tile-body">
                <div className="admin-tool-tile-title">Dev Snapshot{snapshotting && ' — running…'}</div>
                <div className="admin-tool-tile-desc">{snapshotting ? 'Copying prod DB — this can take several minutes' : 'Copy prod DB to this environment — start coding from live state'}</div>
              </div>
            </button>
            )}
          </div>
        </div>
      </div>
      </>}

      </div>

      {/* Right column */}
      <div>

      {/* Theme Selector */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h3>Theme</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            {[
              { id: 'light', label: 'CM Light', colors: ['#ffffff', '#0052F6', '#92752E'], icon: '\u2600' },
              { id: 'dark', label: 'CM Dark', colors: ['#00193d', '#007BDB', '#c9a14a'], icon: '\uD83C\uDF19' },
              { id: 'vibe', label: "Vibin'", colors: ['#0a0118', '#00e5ff', '#ff1493'], icon: '\uD83D\uDE0E' },
              { id: 'hippie', label: 'Groovy', colors: ['#0052F6', '#92752E', '#f9a825'], icon: '\u270C\uFE0F' },
              { id: 'memorial', label: 'Memorial Day', colors: ['#B22234', '#ffffff', '#0A3161'], icon: '\uD83C\uDF87' },
            ]
            .filter(t => t.id !== 'memorial' || memorialState.state !== 'hidden' || user?.isAdmin)
            .map(theme => {
              return (
                <div key={theme.id} onClick={() => {
                  localStorage.setItem('bidboard-theme', theme.id);
                  localStorage.setItem('bidboard-theme-chosen-at', new Date().toISOString());
                  document.documentElement.classList.remove('dark-mode', 'hippie-mode', 'memorial-mode', 'vibe-mode');
                  if (theme.id === 'dark') document.documentElement.classList.add('dark-mode');
                  if (theme.id === 'hippie') document.documentElement.classList.add('hippie-mode');
                  if (theme.id === 'memorial') document.documentElement.classList.add('memorial-mode');
                  if (theme.id === 'vibe') document.documentElement.classList.add('vibe-mode');
                  setCurrentTheme(theme.id);
                  window.dispatchEvent(new Event('theme-change'));
                }} style={{
                  padding: '16px',
                  borderRadius: '8px',
                  border: currentTheme === theme.id ? '2px solid var(--color-accent)' : '2px solid var(--color-gray-200)',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                  background: currentTheme === theme.id ? 'rgba(0, 82, 246, 0.04)' : 'transparent',
                }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>{theme.icon}</div>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>{theme.label}</div>
                  {theme.sub && <div style={{ fontSize: '10px', color: 'var(--color-gray-500)', fontStyle: 'italic', marginTop: '2px' }}>{theme.sub}</div>}
                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '8px' }}>
                    {theme.colors.map((c, i) => (
                      <div key={i} style={{ width: '16px', height: '16px', borderRadius: '50%', background: c, border: '1px solid #ccc' }} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Memorial Day theme admin control moved to /admin/themes. */}

      {/* System Diagnostics */}
      <div className="card">
        <div className="card-header">
          <h3>System Diagnostics</h3>
          <span style={{ fontSize: '10px', fontFamily: 'monospace', color: '#777' }}>PID {statusData?.server?.nodeVersion || '...'}</span>
        </div>
        <div className="card-body" style={{ padding: '16px', fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: '11px' }}>
          {!statusData ? (
            <div style={{ color: '#777' }}>Initializing health check daemon...</div>
          ) : (
            <>
              {/* Overall Health */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', padding: '8px 12px', background: statusData.database?.status === 'connected' ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', borderRadius: '6px', border: `1px solid ${statusData.database?.status === 'connected' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}` }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  backgroundColor: statusData.database?.status === 'connected' ? '#22c55e' : '#ef4444',
                  boxShadow: `0 0 6px ${statusData.database?.status === 'connected' ? '#22c55e' : '#ef4444'}`,
                }} />
                <span style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px' }}>
                  {statusData.database?.status === 'connected' ? 'ALL SERVICES NOMINAL' : 'CRITICAL: SERVICE DEGRADATION DETECTED'}
                </span>
              </div>

              {/* Diagnostics Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
                {/* Application Runtime */}
                <div style={{ background: 'var(--color-gray-50, #f9fafb)', borderRadius: '6px', padding: '10px', borderLeft: '3px solid #22c55e' }}>
                  <div style={{ fontSize: '9px', fontWeight: 700, color: '#777', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Application Runtime</div>
                  <div style={{ lineHeight: 1.8, color: 'var(--color-gray-700)' }}>
                    <div>proc.uptime: <span style={{ color: '#22c55e' }}>{Math.floor(statusData.uptime / 86400)}d {Math.floor((statusData.uptime % 86400) / 3600)}h {Math.floor((statusData.uptime % 3600) / 60)}m {statusData.uptime % 60}s</span></div>
                    <div>runtime: <span style={{ color: 'var(--color-accent)' }}>{statusData.server?.nodeVersion}</span></div>
                    <div>heap.used: <span style={{ color: '#f59e0b' }}>{statusData.server?.memory?.used} MB</span> / {statusData.server?.memory?.total} MB</div>
                    <div>host: <span style={{ color: '#8b5cf6' }}>{statusData.server?.hostname}</span></div>
                  </div>
                </div>

                {/* Database Engine */}
                <div style={{ background: 'var(--color-gray-50, #f9fafb)', borderRadius: '6px', padding: '10px', borderLeft: `3px solid ${statusData.database?.status === 'connected' ? '#22c55e' : '#ef4444'}` }}>
                  <div style={{ fontSize: '9px', fontWeight: 700, color: '#777', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Database Engine</div>
                  <div style={{ lineHeight: 1.8, color: 'var(--color-gray-700)' }}>
                    <div>conn.state: <span style={{ color: statusData.database?.status === 'connected' ? '#22c55e' : '#ef4444' }}>{statusData.database?.status === 'connected' ? 'ESTABLISHED' : 'REFUSED'}</span></div>
                    <div>server: <span style={{ color: 'var(--color-accent)' }}>{statusData.database?.server}:1433</span></div>
                    <div>schema: <span style={{ color: '#8b5cf6' }}>{statusData.database?.database}.precon</span></div>
                    <div>records.projects: <span style={{ color: '#f59e0b' }}>{statusData.database?.projectCount ?? 0}</span></div>
                  </div>
                </div>

                {/* AI Inference Engine */}
                <div style={{ background: 'var(--color-gray-50, #f9fafb)', borderRadius: '6px', padding: '10px', borderLeft: `3px solid ${statusData.claudeApi?.status === 'configured' ? '#22c55e' : '#eab308'}` }}>
                  <div style={{ fontSize: '9px', fontWeight: 700, color: '#777', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>AI Inference Engine</div>
                  <div style={{ lineHeight: 1.8, color: 'var(--color-gray-700)' }}>
                    <div>api.auth: <span style={{ color: statusData.claudeApi?.status === 'configured' ? '#22c55e' : '#eab308' }}>{statusData.claudeApi?.status === 'configured' ? 'AUTHENTICATED' : 'NO_CREDENTIALS'}</span></div>
                    <div>model: <span style={{ color: 'var(--color-accent)' }}>claude-sonnet-4-6</span></div>
                    <div>tokens.total: <span style={{ color: '#f59e0b' }}>{(statusData.claudeApi?.totalTokens ?? 0).toLocaleString()}</span></div>
                    <div>tokens.io: <span style={{ color: '#777' }}>{(statusData.claudeApi?.totalInputTokens ?? 0).toLocaleString()}in / {(statusData.claudeApi?.totalOutputTokens ?? 0).toLocaleString()}out</span></div>
                  </div>
                </div>

                {/* SLA / Availability */}
                <div style={{ background: 'var(--color-gray-50, #f9fafb)', borderRadius: '6px', padding: '10px', borderLeft: '3px solid #22c55e' }}>
                  <div style={{ fontSize: '9px', fontWeight: 700, color: '#777', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>SLA / Availability</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#22c55e', letterSpacing: '-1px' }}>
                    {uptimeHistory?.overallPercentage != null
                      ? `${uptimeHistory.overallPercentage.toFixed(3)}%`
                      : '100.000%'}
                  </div>
                  <div style={{ lineHeight: 1.8, color: 'var(--color-gray-700)', marginTop: '4px' }}>
                    <div>target.sla: <span style={{ color: '#22c55e' }}>99.900%</span></div>
                    <div>window: <span style={{ color: '#777' }}>90d rolling</span></div>
                  </div>
                </div>
              </div>

              {/* Uptime History Bar Chart */}
              <div>
                <div style={{ fontSize: '9px', fontWeight: 700, color: '#777', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Availability Timeline — 90 Day Window</div>
                {uptimeHistory?.days && uptimeHistory.days.length > 0 ? (
                  <>
                    <UptimeBar days={uptimeHistory.days} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                      <span style={{ fontSize: '9px', color: '#999' }}>{uptimeHistory.days[0]?.date}</span>
                      <span style={{ fontSize: '9px', color: '#999' }}>today</span>
                    </div>
                  </>
                ) : (
                  <div style={{ color: '#777', fontSize: '10px', padding: '8px 0' }}>Collecting telemetry data... First data point in ~5 min.</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      </div>
      </div>

      {/* Changelog — collapsed by default */}
      <div className="card" style={{ marginTop: '20px' }}>
        <div className="card-header" onClick={() => setChangelogOpen(o => !o)}
          style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              style={{ transition: 'transform 0.2s', transform: changelogOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
              <polyline points="9 18 15 12 9 6"/>
            </svg>
            Changelog
          </h3>
          <a href="https://git.cmcorp.co/bschramm/bidboard/commits/branch/dev" target="_blank" rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ fontSize: '11px', color: 'var(--color-accent)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
            View on Gitea
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </a>
        </div>
        {changelogOpen && (
          <div className="card-body" style={{ padding: 0, maxHeight: '400px', overflowY: 'auto' }}>
            {!changelog ? (
              <div style={{ padding: '16px', color: '#777', fontSize: '12px' }}>Loading changelog...</div>
            ) : changelog.length === 0 ? (
              <div style={{ padding: '16px', color: '#777', fontSize: '12px' }}>No commits found</div>
            ) : (() => {
              const grouped = {};
              changelog.forEach(c => {
                const day = new Date(c.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
                if (!grouped[day]) grouped[day] = [];
                grouped[day].push(c);
              });
              return Object.entries(grouped).map(([day, commits]) => (
                <div key={day}>
                  <div style={{ padding: '6px 16px', fontSize: '10px', fontWeight: 700, color: '#777', textTransform: 'uppercase', letterSpacing: '0.5px', background: 'var(--color-gray-50, #f9fafb)', borderBottom: '1px solid var(--color-gray-100)', position: 'sticky', top: 0, zIndex: 1 }}>
                    {day}
                  </div>
                  {commits.map(c => (
                    <div key={c.hash} style={{ padding: '8px 16px', borderBottom: '1px solid var(--color-gray-100)', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <a href={c.url} target="_blank" rel="noopener noreferrer"
                        style={{ fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: '11px', color: 'var(--color-accent)', textDecoration: 'none', flexShrink: 0, marginTop: '1px' }}>
                        {c.hash}
                      </a>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', lineHeight: 1.4, wordBreak: 'break-word' }}>{c.message}</div>
                        <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                          {c.author} &middot; {new Date(c.date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          {c.files > 0 && <> &middot; {c.files} file{c.files !== 1 ? 's' : ''}</>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ));
            })()}
          </div>
        )}
      </div>

    </div>
    </>
  );
}
